import { AcceptTeamInviteMutation, TeamInviteAcceptInput } from "@/graphql/generated/graphql";
import { createOperationClient } from "@/services/react-query.service/graphql-client";
import { queryKeys } from "@/services/react-query.service/query-keys";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { acceptTeamInviteMutationDocument } from "./team-membership.gql";
import { EVENT_TEAM_INVITE_ACCEPTED, trackTeamEvent } from "./tracking";

export interface UseAcceptTeamMembershipInviteProps {
  team: {
    id: string;
  };
  onCompleted?: () => void;
  onError?: (e: Error) => void;
}

export const useTeamInviteAccept = ({ onCompleted, onError }: UseAcceptTeamMembershipInviteProps) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (input: TeamInviteAcceptInput): Promise<AcceptTeamInviteMutation> => {
      const client = createOperationClient("acceptTeamInvite");
      return await client.request(acceptTeamInviteMutationDocument, { input });
    },
    onSuccess: (data) => {
      const teamMembership = data?.acceptTeamInvite;
      if (teamMembership) {
        trackTeamEvent(EVENT_TEAM_INVITE_ACCEPTED, teamMembership);
      }

      // Invalidate team membership queries
      queryClient.invalidateQueries({
        queryKey: queryKeys.teams.details(),
      });

      if (onCompleted) {
        onCompleted();
      }
    },
    onError: (error) => {
      if (onError) {
        onError(error);
      }
    },
  });

  return {
    queryResult: mutation,
    acceptTeamInvitation: mutation.mutate,
    loading: mutation.isPending,
    error: mutation.error,
  };
};
